import User, { USER_ROLES } from "../../../classes/User";
import Address from "../../../classes/Address";
import { auth, db } from "../../../boot/firebase";
import { updatePassword } from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit as limitQuery,
  orderBy,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { call } from "../../../util/misc";
import { i18n } from "../../../boot/i18n";

export const GET_USER = ({ dispatch }, id) => {
  return dispatch("GET_DOC", {
    converter: User,
    path: User.getPath(id),
    stateKey: "users",
    id,
  });
};

// Whether a contact has already registered a user account. Used to decide
// whether a customer link should invite them to register.
export const HAS_USER_ACCOUNT = async (context, id) => {
  if (!id) {
    return false;
  }
  const snapshot = await getDoc(doc(db, User.getPath(id)));
  return snapshot.exists();
};

export const GET_USERS = (
  { dispatch },
  { conditions, limitCount, pagination }
) => {
  return dispatch("GET_COLLECTION", {
    converter: User,
    stateKey: "users",
    path: User.getPath(),
    conditions,
    limitCount,
    pagination,
  });
};

export const SEARCH_USERS = async (
  { commit, state },
  { search, limit = 20, type = null }
) => {
  if (!search) {
    const constraints = [orderBy("created", "desc"), limitQuery(limit)];
    if (type) {
      constraints.unshift(
        where("type", Array.isArray(type) ? "in" : "==", type)
      );
    }
    const snap = await getDocs(
      query(collection(db, User.getPath()), ...constraints)
    );
    return snap.docs.map((docSnapshot) => {
      const user = new User(docSnapshot.id, docSnapshot.data());
      commit("SET", { stateKey: "users", stateID: docSnapshot.id, doc: user });
      return user;
    });
  }

  let matches = Object.values(state.users || {});
  if (type) {
    matches = matches.filter((user) =>
      Array.isArray(type)
        ? type.includes(user.data.type)
        : user.data.type === type
    );
  }

  const searchLower = search.toLowerCase();
  matches = matches.filter((user) => {
    if (user.data.name?.toLowerCase().includes(searchLower)) return true;
    if (user.data.company?.toLowerCase().includes(searchLower)) return true;
    if (
      user.data.position?.some((position) =>
        position.toLowerCase().includes(searchLower)
      )
    ) {
      return true;
    }
    if (
      user.data.emails?.some((email) =>
        email.address?.toLowerCase().includes(searchLower)
      )
    ) {
      return true;
    }
    if (user.data.phones?.some((phone) => phone.number?.includes(search))) {
      return true;
    }
    return false;
  });

  return matches.slice(0, limit);
};

export const LISTEN_USER = ({ dispatch }, { id, next, error, unlisten }) => {
  return dispatch("LISTEN_DOC", {
    converter: User,
    path: User.getPath(id),
    stateKey: "users",
    id,
    next,
    error,
    unlisten,
  });
};

export const LISTEN_CURRENT_USER = ({ dispatch, commit, state }) => {
  if (!state.user || !state.user.id) return;

  let previousRoles = state.user.data?.roles ? [...state.user.data.roles] : [];
  const didRolesChange = (before, after) =>
    before.length !== after.length ||
    before.some((role) => !after.includes(role));

  return dispatch("LISTEN_DOC", {
    converter: User,
    path: User.getPath(state.user.id),
    stateKey: "users",
    id: state.user.id,
    next: async (user) => {
      const currentRoles = user.data.roles || [];
      const newRoles = currentRoles.filter(
        (role) => !previousRoles.includes(role)
      );
      const rolesChanged = didRolesChange(previousRoles, currentRoles);

      let claims = state.claims || {};
      const expectedAdmin = currentRoles.includes("admin");
      const expectedSuper =
        expectedAdmin || currentRoles.includes("management");
      const expectedCustomer = currentRoles.includes("customer");
      const expectedEmployee = !expectedCustomer;
      const claimsOutOfSync =
        claims.admin !== expectedAdmin ||
        claims.super !== expectedSuper ||
        claims.customer !== expectedCustomer ||
        claims.employee !== expectedEmployee ||
        didRolesChange(claims.roles || [], currentRoles);

      if ((rolesChanged || claimsOutOfSync) && auth.currentUser) {
        await auth.currentUser.getIdToken(true);
        claims = (await auth.currentUser.getIdTokenResult()).claims;
      }

      if (newRoles.length > 0 && previousRoles.length > 0) {
        dispatch("PROMPT_NEW_ROLE_WIDGETS", { newRoles, user });
      }

      previousRoles = [...currentRoles];
      commit("SET_CURRENT_USER", { user, claims });
    },
    error: (error) => {
      console.error("Error listening to current user:", error);
    },
  });
};

export const PROMPT_NEW_ROLE_WIDGETS = ({ dispatch, state }, { newRoles }) => {
  const currentWidgets = state.user.widgets || [];
  const newWidgets = [];

  newRoles.forEach((role) => {
    const roleConfig = USER_ROLES[role];
    roleConfig?.widgets?.forEach((widget) => {
      if (!currentWidgets.includes(widget) && !newWidgets.includes(widget)) {
        newWidgets.push(widget);
      }
    });
  });

  if (!newWidgets.length) return;

  const roleLabels = newRoles
    .map((role) => USER_ROLES[role]?.label || role)
    .join(", ");

  dispatch("PROMPT", {
    title: i18n.global.t("New Role Assigned"),
    message: i18n.global.t("confirm.new-role-widgets", { roles: roleLabels }),
    success: async () => {
      await dispatch("SAVE_PREFERENCES", {
        type: "dashboardWidgets",
        data: [...currentWidgets, ...newWidgets],
      });
      dispatch("NOTIFY", {
        message: i18n.global.t("Dashboard widgets updated"),
        color: "positive",
      });
    },
  });
};

export const LISTEN_USERS = ({ dispatch }, { next, error, unlisten }) => {
  return dispatch("LISTEN_COLLECTION", {
    converter: User,
    path: User.getPath(),
    stateKey: "users",
    next,
    error,
    unlisten,
  });
};

export const REGISTER_CUSTOMER = async (context, { uid, email, password }) => {
  return call("registerCustomer", {
    uid,
    email,
    password,
  });
};

export const CREATE_USER = async (context, { uid, password, user }) => {
  return call("createUser", {
    uid,
    password,
    data: user.data,
  });
};

export const DISABLE_USER = async (context, { uid, reason }) => {
  return call("disableUser", {
    uid,
    reason,
  });
};

export const ENABLE_USER = async (context, { uid }) => {
  return call("enableUser", {
    uid,
  });
};

export const UPDATE_USER = async (
  { commit, state },
  { password, user, info }
) => {
  if (!info) {
    info = user.data;
  }
  info.modified = new Date();
  info.modifiedBy = state.user.id;

  const userID = user && user.id ? user.id : auth.currentUser.uid;

  // If this save resolves a pending "add new address" request for this user
  // (launched from a ContactAddress field), detect the newly added address so
  // the originating field can auto-populate it on return.
  const applyPendingAddress = () => {
    const pendingAddress = state.pendingAddressSelection;
    if (
      !pendingAddress ||
      pendingAddress.contactID !== userID ||
      pendingAddress.address
    ) {
      return;
    }
    const existingKeys = new Set(pendingAddress.existingKeys || []);
    const newAddress = (info.addresses || []).find(
      (address) => !existingKeys.has(new Address(address).locationKey())
    );
    if (newAddress) {
      commit("SET_PENDING_ADDRESS", {
        ...newAddress,
        contact: userID,
      });
    }
  };

  let path;
  if (!user || user.id === auth.currentUser.uid) {
    // User is currentUser
    const currentUser = auth.currentUser;
    path = User.getPath(currentUser.uid);

    // Update own password
    if (password) {
      await updatePassword(currentUser, password);
    }

    // Update other info. A new email address needs nothing more: the
    // updateUser trigger (functions/users.js) copies emails[0] to the sign-in
    // email, as it does for other users. (Firebase rejects updateEmail while
    // email enumeration protection is on.)
    await updateDoc(doc(db, path), info);
    applyPendingAddress();

    // Update current user
    const claims = (await currentUser.getIdTokenResult()).claims;
    return commit("SET_CURRENT_USER", {
      user: new User(user.id, info),
      claims,
    });
  } else {
    // Update other user's password
    path = user.path;
    if (password) {
      await call("setUserPassword", {
        uid: user.id,
        password,
      });
    }

    // Update other info
    await updateDoc(doc(db, path), info);
    applyPendingAddress();
  }
};

// `uid` defaults to the current user. Another user's doc only takes the
// preferences the rules allow (Defer Ordering to Purchasers).
export const SAVE_PREFERENCES = (
  context,
  { type, data, uid = auth.currentUser.uid }
) => {
  return updateDoc(doc(db, User.getPath(uid)), {
    modifiedBy: auth.currentUser.uid,
    ["preferences." + type]: data,
  });
};
